import db from '../db/db'
import Database from '../types/Database'

/**
 * PeopleService
 *
 * Service class for people management operations within an office.
 * Handles office user listing, manager assignments, and absence reporting.
 */
export class PeopleService {
  /**
   * Get all users who belong to an office.
   * Queries users who have officeId in their officeids array.
   *
   * @param officeId - The office ID to get users for
   * @returns Array of users belonging to the office
   */
  static async getOfficeUsers(officeId: string): Promise<Database.User[]> {
    try {
      if (!officeId) {
        return []
      }

      // Query users who have this officeId in their officeids array
      const users = await db.query({
        collection: 'users',
        where: ['officeids', 'array-contains', officeId]
      })

      return users
    } catch (error) {
      console.error('Error fetching office users:', error)
      throw new Error('Failed to load office users. Please try again.')
    }
  }

  /**
   * Assign a manager to a user within an office context.
   * Creates or updates an OfficeUserManager record.
   *
   * @param officeId - The office ID
   * @param userId - The user being managed
   * @param managerId - The manager being assigned
   * @param assignedBy - The user performing the assignment
   * @returns The created OfficeUserManager record reference
   */
  static async assignManager(
    officeId: string,
    userId: string,
    managerId: string,
    assignedBy: string
  ): Promise<void> {
    try {
      if (!officeId || !userId || !managerId || !assignedBy) {
        throw new Error('All parameters are required for manager assignment')
      }

      // Check if there is already a manager assignment for this user in this office
      const existing = await db.query({
        collection: 'officeUserManagers',
        where: [
          ['officeId', '==', officeId],
          ['userId', '==', userId]
        ]
      })

      if (existing.length > 0) {
        // Update the existing assignment
        await db.updateItem('officeUserManagers', existing[0].id, {
          managerId,
          assignedBy,
          assignedAt: new Date()
        })
      } else {
        // Create a new assignment
        const record: Omit<Database.OfficeUserManager, 'id'> = {
          officeId,
          userId,
          managerId,
          assignedBy,
          assignedAt: new Date()
        }

        await db.addItem('officeUserManagers', record)
      }
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      if (errorInstance.message.includes('All parameters are required')) {
        throw errorInstance
      }

      console.error('Error assigning manager:', error)
      throw new Error('Failed to assign manager. Please try again.')
    }
  }

  /**
   * Remove a manager assignment.
   *
   * @param id - The OfficeUserManager record ID to delete
   */
  static async removeManagerAssignment(id: string): Promise<void> {
    try {
      if (!id) {
        throw new Error('Assignment ID is required')
      }

      await db.deleteItem('officeUserManagers', id)
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      if (errorInstance.message.includes('Assignment ID is required')) {
        throw errorInstance
      }

      console.error('Error removing manager assignment:', error)
      throw new Error('Failed to remove manager assignment. Please try again.')
    }
  }

  /**
   * Get all manager assignments for an office.
   *
   * @param officeId - The office ID
   * @returns Array of OfficeUserManager records for the office
   */
  static async getManagerAssignments(
    officeId: string
  ): Promise<Database.OfficeUserManager[]> {
    try {
      if (!officeId) {
        return []
      }

      const assignments = await db.query({
        collection: 'officeUserManagers',
        where: ['officeId', '==', officeId]
      })

      return assignments
    } catch (error) {
      console.error('Error fetching manager assignments:', error)
      throw new Error('Failed to load manager assignments. Please try again.')
    }
  }

  /**
   * Get all users who report to a specific manager in an office.
   *
   * @param officeId - The office ID
   * @param managerId - The manager's user ID
   * @returns Array of OfficeUserManager records where managerId matches
   */
  static async getMyReports(
    officeId: string,
    managerId: string
  ): Promise<Database.OfficeUserManager[]> {
    try {
      if (!officeId || !managerId) {
        return []
      }

      const reports = await db.query({
        collection: 'officeUserManagers',
        where: [
          ['officeId', '==', officeId],
          ['managerId', '==', managerId]
        ]
      })

      return reports
    } catch (error) {
      console.error('Error fetching reports:', error)
      throw new Error('Failed to load reports. Please try again.')
    }
  }

  /**
   * Report an absence for a user.
   *
   * @param data - The absence report data (without id)
   * @returns The created absence report reference
   */
  static async reportAbsence(
    data: Omit<Database.AbsenceReport, 'id'>
  ): Promise<void> {
    try {
      if (!data.officeId || !data.userId || !data.reportedBy || !data.date) {
        throw new Error('Required fields are missing for absence report')
      }

      if (data.type === 'partial_day' && data.hours == null) {
        throw new Error('Hours must be specified for partial day absences')
      }

      await db.addItem('absenceReports', data)
    } catch (error) {
      const errorInstance =
        error instanceof Error ? error : new Error(String(error))

      if (
        errorInstance.message.includes('Required fields are missing') ||
        errorInstance.message.includes('Hours must be specified')
      ) {
        throw errorInstance
      }

      console.error('Error reporting absence:', error)
      throw new Error('Failed to report absence. Please try again.')
    }
  }

  /**
   * Get absence reports for an office, optionally filtered by user.
   *
   * @param officeId - The office ID
   * @param userId - Optional user ID to filter by
   * @returns Array of AbsenceReport records
   */
  static async getAbsenceReports(
    officeId: string,
    userId?: string
  ): Promise<Database.AbsenceReport[]> {
    try {
      if (!officeId) {
        return []
      }

      if (userId) {
        const reports = await db.query({
          collection: 'absenceReports',
          where: [
            ['officeId', '==', officeId],
            ['userId', '==', userId]
          ],
          orderBy: ['date', 'desc']
        })
        return reports
      }

      const reports = await db.query({
        collection: 'absenceReports',
        where: ['officeId', '==', officeId],
        orderBy: ['date', 'desc']
      })

      return reports
    } catch (error) {
      console.error('Error fetching absence reports:', error)
      throw new Error('Failed to load absence reports. Please try again.')
    }
  }
}

export default PeopleService
